import {
  ManuscriptOwnership,
  libraryRoot,
  libraryScopes,
} from "./library-ownership";
import type {
  RefineUploadProvider,
  UploadChannel,
} from "./library-refine-upload";
import { ReviewSummary } from "./manuscript-review";
import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type DragEvent,
  type MouseEvent,
  type KeyboardEvent,
} from "react";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ChevronRight,
  Folder,
  FolderOpen,
  FileText,
  HardDrive,
  LayoutGrid,
  List,
  PanelRight,
  Plus,
  FolderPlus,
  Upload,
  LockKeyhole,
  MoreHorizontal,
  Scissors,
  ClipboardPaste,
  Search,
  RefreshCw,
  X,
} from "lucide-react";
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  Field,
  Select,
  Checkbox,
} from "../../design-system";
import type {
  LibraryAdapter,
  LibraryItem,
  LibraryFolderRow,
  LibraryStatus,
} from "./library-adapter";
import {
  libraryStatus,
  descendantIds,
  useLibraryAdapter,
  type LibraryExample,
} from "./manuscript-library-data";
import {
  useLibraryList,
  useLibraryResource,
  invalidateLibrary,
  libraryError,
} from "./library-runtime";
import { LibraryPreview, LibraryMedia } from "./library-media";
import {
  LibraryFolderTree,
  folderCounts,
  libraryDragType,
  droppedFiles,
  type LibraryTransfer,
} from "./library-tree";
import "./library-explorer.css";
export type ExplorerExample =
  | "folder-create"
  | "folder-inherited"
  | "delete-refined"
  | "unfiled"
  | "root"
  | "details"
  | "icons"
  | "preview"
  | "selected"
  | "drag"
  | "menu"
  | "rename"
  | "empty"
  | "locked"
  | "upload"
  | "detail"
  | "editor"
  | "mobile"
  | "move";
export type LibraryExplorerProps = {
  refineUpload?: RefineUploadProvider;
  initialUploadChannel?: UploadChannel;
  initialDocumentId?: string;
  onRefine?: (customerId: string, folderId: string | null) => void;
  onOpenRefine?: (id: string) => void;
  customerId?: string;
  canWrite?: boolean;
  canSend?: boolean;
  example?: LibraryExample;
  explorerExample?: ExplorerExample;
  onDirtyChange?: (dirty: boolean) => void;
  initialSendStep?: number;
  initialSendIds?: string[];
  provider?: LibraryAdapter;
};
type Row = {
  key: string;
  kind: "folder" | "manuscript";
  id: string;
  name: string;
  item?: LibraryItem;
  folder?: LibraryFolderRow;
};
type Location = { customer: string; folder: string };
type Doc = { id: string; edit?: boolean };
type Selection = { customerId: string; entries: Row[] };
const mobile = () => window.matchMedia("(max-width: 767px)").matches;
function Status({ item }: { item: LibraryItem }) {
  return (
    <Badge
      tone={
        item.status === "ready"
          ? "teal"
          : item.status === "draft"
            ? "amber"
            : item.locked
              ? "blue"
              : "neutral"
      }
    >
      {item.locked && <LockKeyhole size={11} />} {libraryStatus[item.status]}
    </Badge>
  );
}
function Thumbnail({
  adapter,
  item,
}: {
  adapter: LibraryAdapter;
  item: LibraryItem;
}) {
  const detail = useLibraryResource(adapter, "detail:" + item.id, () =>
    adapter.detail(item.id),
  );
  const first = detail.data?.files.find((f) =>
    /\.(png|jpe?g|gif|webp|heic)$/i.test(f.name),
  );
  return first ? (
    <LibraryMedia
      provider={adapter}
      customerId={item.customerId}
      file={first.fileId}
      name={first.name}
    />
  ) : (
    <FileText size={42} />
  );
}
export function LibraryExplorer({
  customerId: fixedCustomer,
  canWrite = true,
  canSend = true,
  example = "normal",
  explorerExample = "details",
  onDirtyChange,
  initialSendStep = 0,
  initialSendIds,
  provider,
  onRefine,
  refineUpload,
  initialUploadChannel,
  initialDocumentId,
  renderDocument,
  renderUpload,
  renderSend,
}: LibraryExplorerProps & {
  renderDocument: (p: {
    provider: LibraryAdapter;
    customerId: string;
    id: string;
    edit: boolean;
    onDirtyChange: (dirty: boolean) => void;
  }) => ReactNode;
  renderUpload: (p: {
    provider: LibraryAdapter;
    customerId: string;
    folderId: string | null;
    files?: File[];
    channel?: UploadChannel;
    onClose: () => void;
  }) => ReactNode;
  renderSend: (p: {
    provider: LibraryAdapter;
    customerId: string;
    ids: string[];
    step: number;
    onClose: () => void;
  }) => ReactNode;
}) {
  const adapter = useLibraryAdapter(provider, example);
  const scopes = libraryScopes(adapter);
  const firstCustomer =
    adapter.defaultScopeId ?? fixedCustomer ?? adapter.customers[0]?.id ?? "";
  const initialFolder =
    explorerExample === "folder-inherited"
      ? "target-onyou"
      : explorerExample === "folder-create"
        ? "all"
        : explorerExample === "delete-refined"
          ? "folder-campaign"
          : explorerExample === "unfiled"
            ? "none"
            : explorerExample === "root"
              ? "all"
              : example === "empty" || explorerExample === "empty"
                ? "folder-empty"
                : (provider && !adapter.scopes) ||
                    (!adapter.scopes && firstCustomer !== "onyou")
                  ? "all"
                  : ["selected", "preview", "menu", "rename", "move"].includes(
                        explorerExample,
                      )
                    ? "folder-campaign"
                    : adapter.scopes
                      ? "all"
                      : "folder-oct";
  const [history, setHistory] = useState<Location[]>([
      { customer: firstCustomer, folder: initialFolder },
    ]),
    [cursor, setCursor] = useState(0);
  const location = history[cursor],
    { customer, folder } = location;
  const write =
    canWrite &&
    example !== "readonly" &&
    !scopes.find((s) => s.id === customer)?.readOnly &&
    !scopes.find((s) => s.id === customer)?.ownerCustomerId;
  const [folderTarget, setFolderTarget] = useState("");
  const [query, setQuery] = useState(""),
    [status, setStatus] = useState("all"),
    [deep, setDeep] = useState(false);
  const [view, setView] = useState(
      explorerExample === "icons" ? "icons" : "details",
    ),
    [preview, setPreview] = useState(true);
  const [selected, setSelected] = useState<string[]>(
      ["selected", "menu", "move"].includes(explorerExample)
        ? ["m:ms-1", "m:ms-2"]
        : explorerExample === "preview"
          ? ["m:ms-1"]
          : [],
    ),
    [anchor, setAnchor] = useState("");
  const initialId =
    example === "history"
      ? "ms-5"
      : example === "invalid"
        ? "ms-3"
        : example === "locked" || explorerExample === "locked"
          ? "ms-5"
          : example === "duplicate"
            ? "ms-6"
            : "ms-1";
  const [doc, setDoc] = useState<Doc | null>(
    initialDocumentId
      ? { id: initialDocumentId, edit: false }
      : [
            "files",
            "versions",
            "history",
            "editor",
            "conflict",
            "invalid",
            "locked",
            "duplicate",
          ].includes(example) ||
          ["detail", "editor", "locked"].includes(explorerExample)
        ? {
            id: initialId,
            edit:
              example === "editor" ||
              example === "conflict" ||
              explorerExample === "editor",
          }
        : null,
  );
  const [dirty, setDirty] = useState(false),
    [confirmClose, setConfirmClose] = useState(false);
  const [upload, setUpload] = useState<{
    files?: File[];
    channel?: UploadChannel;
  } | null>(
    ["upload", "batch"].includes(example) || explorerExample === "upload"
      ? {}
      : null,
  );
  const [sendIds, setSendIds] = useState<string[] | null>(
    initialSendIds ?? (example === "send" ? ["ms-1", "ms-2", "ms-6"] : null),
  );
  const [dialog, setDialog] = useState<
    "" | "create" | "rename" | "delete" | "move" | "properties"
  >(
    ["folder-create", "folder-inherited"].includes(explorerExample)
      ? "create"
      : explorerExample === "delete-refined"
        ? "delete"
        : explorerExample === "rename"
          ? "rename"
          : explorerExample === "move"
            ? "move"
            : "",
  );
  const [targets, setTargets] = useState<Row[]>(
      explorerExample === "delete-refined"
        ? [
            {
              key: "m:ms-1",
              kind: "manuscript",
              id: "ms-1",
              name: "진료 예약 안내",
            },
          ]
        : explorerExample === "rename"
          ? [
              {
                key: "f:folder-campaign",
                kind: "folder",
                id: "folder-campaign",
                name: "가을 캠페인",
              },
            ]
          : [],
    ),
    [name, setName] = useState(
      explorerExample === "rename" ? "가을 캠페인" : "",
    ),
    [moveTo, setMoveTo] = useState<string | null>(null),
    [memo, setMemo] = useState<string | null>(null),
    [keywords, setKeywords] = useState<string | null>(null);
  const [error, setError] = useState(""),
    [note, setNote] = useState(""),
    [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [clipboard, setClipboard] = useState<Selection | null>(null),
    [drag, setDrag] = useState<LibraryTransfer | null>(
      explorerExample === "drag"
        ? {
            customerId: customer,
            entries: [{ id: "ms-5", kind: "manuscript" }],
          }
        : null,
    ),
    [over, setOver] = useState<string | null>(null),
    [external, setExternal] = useState(false);
  const [context, setContext] = useState<{
    x: number;
    y: number;
    rows: Row[];
  } | null>(explorerExample === "menu" ? { x: 500, y: 310, rows: [] } : null);
  const [collapsedDrives, setCollapsedDrives] = useState<string[]>([]);
  const area = useRef<HTMLDivElement>(null),
    menu = useRef<HTMLDivElement>(null),
    lastFocus = useRef<HTMLElement | null>(null),
    timer = useRef<ReturnType<typeof setTimeout> | null>(null),
    longPress = useRef(false);
  const listing = useLibraryList(adapter, {
    customerId: customer,
    folderId: folder === "all" && !deep ? "none" : folder,
    deep,
    status: status === "all" ? undefined : (status as LibraryStatus),
    q: query || undefined,
  });
  const folderData = useLibraryResource(adapter, "folders:" + customer, () =>
    adapter.folders(customer),
  );
  const folders = folderData.data?.folders ?? [];
  const current = folders.find((f) => f.id === folder);
  const totals = useLibraryResource(
    adapter,
    JSON.stringify(["explorer-ready", customer, folder, query, deep]),
    async () =>
      (
        await adapter.list({
          customerId: customer,
          folderId: folder === "all" && !deep ? "none" : folder,
          deep,
          status: "ready",
          q: query || undefined,
          offset: 0,
          limit: 1,
        })
      ).total,
  );
  const [sort, setSort] = useState("name"),
    [descending, setDescending] = useState(false);
  const rows: Row[] = [
    ...(folder === "none"
      ? []
      : folders
          .filter(
            (f) =>
              f.parentId === (current?.id ?? null) &&
              (!query ||
                f.name.toLocaleLowerCase().includes(query.toLocaleLowerCase())),
          )
          .map((f) => ({
            key: "f:" + f.id,
            kind: "folder" as const,
            id: f.id,
            name: f.name,
            folder: f,
          }))),
    ...listing.items.map((item) => ({
      key: "m:" + item.id,
      kind: "manuscript" as const,
      id: item.id,
      name: item.title,
      item,
    })),
  ].sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === "folder" ? -1 : 1;
    const value = (r: Row): string | number =>
      sort === "status"
        ? (r.item?.status ?? "")
        : sort === "check"
          ? (r.item?.errors ?? 0)
          : sort === "media"
            ? (r.item?.fileCount ?? 0)
            : sort === "version"
              ? (r.item?.revision ?? 0)
              : sort === "job"
                ? (r.item?.postedAt ?? r.item?.lastJobId ?? "")
                : r.name;
    const x = value(a),
      y = value(b);
    return (
      (typeof x === "number" && typeof y === "number"
        ? x - y
        : String(x).localeCompare(String(y), "ko", { numeric: true })) *
      (descending ? -1 : 1)
    );
  });
  const chosen = rows.filter((r) => selected.includes(r.key));
  const primary = chosen.at(-1);
  const detail = useLibraryResource(
    adapter,
    primary?.kind === "manuscript" ? "detail:" + primary.id : null,
    () => adapter.detail(primary!.id),
  );
  const property = useLibraryResource(
    adapter,
    dialog === "properties" && targets[0]?.kind === "manuscript"
      ? "detail:" + targets[0].id
      : null,
    () => adapter.detail(targets[0].id),
  );
  const nameOf = (id: string) => scopes.find((c) => c.id === id)?.name ?? id;
  const path: LibraryFolderRow[] = [];
  let f = current;
  const seen = new Set<string>();
  while (f && !seen.has(f.id)) {
    seen.add(f.id);
    path.unshift(f);
    f = folders.find((x) => x.id === f!.parentId);
  }
  const go = (c: string, f: string) => {
    if (busyRef.current) return;
    setHistory((h) => [...h.slice(0, cursor + 1), { customer: c, folder: f }]);
    setCursor(cursor + 1);
    clearLocation();
  };
  const clearLocation = () => {
    setSelected([]);
    setAnchor("");
    setContext(null);
    setQuery("");
    setError("");
  };
  const back = (delta: number) => {
    if (cursor + delta < 0 || cursor + delta >= history.length) return;
    setCursor(cursor + delta);
    clearLocation();
  };
  const up = () => {
    if (folder !== "all") go(customer, current?.parentId ?? "all");
  };
  const mutate = async (fn: () => Promise<void>) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      setError(libraryError(e));
    } finally {
      invalidateLibrary(adapter);
      busyRef.current = false;
      setBusy(false);
    }
  };
  const select = (
    row: Row,
    event?: Pick<MouseEvent, "ctrlKey" | "metaKey" | "shiftKey">,
  ) => {
    if (event?.shiftKey && anchor) {
      const a = rows.findIndex((r) => r.key === anchor),
        b = rows.indexOf(row);
      setSelected(
        rows
          .slice(Math.max(0, Math.min(a, b)), Math.max(a, b) + 1)
          .map((r) => r.key),
      );
    } else if (event?.ctrlKey || event?.metaKey) {
      setSelected((v) =>
        v.includes(row.key) ? v.filter((k) => k !== row.key) : [...v, row.key],
      );
      setAnchor(row.key);
    } else {
      setSelected([row.key]);
      setAnchor(row.key);
    }
  };
  const open = (row: Row, edit = false) => {
    setContext(null);
    if (row.kind === "folder") go(customer, row.id);
    else {
      lastFocus.current = document.activeElement as HTMLElement;
      setDoc({ id: row.id, edit });
      setConfirmClose(false);
    }
  };
  const closeDoc = () => {
    if (dirty) {
      setConfirmClose(true);
      return;
    }
    setDoc(null);
    requestAnimationFrame(() => lastFocus.current?.focus());
  };
  const actionRows = (row?: Row) =>
    row ? (selected.includes(row.key) ? chosen : [row]) : chosen;
  const showMenu = (event: MouseEvent, row?: Row) => {
    event.preventDefault();
    event.stopPropagation();
    const targets = actionRows(row);
    if (row && !selected.includes(row.key)) select(row);
    setContext({
      x: Math.min(event.clientX, window.innerWidth - 245),
      y: Math.min(event.clientY, window.innerHeight - 460),
      rows: targets,
    });
  };
  useEffect(() => {
    if (!context) return;
    menu.current
      ?.querySelector<HTMLButtonElement>("button:not(:disabled)")
      ?.focus();
    const close = (e: PointerEvent) => {
      if (!menu.current?.contains(e.target as Node)) setContext(null);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [context]);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  const canMove = (
    payload: LibraryTransfer | null,
    target: string | null,
    targetCustomer = customer,
  ) =>
    !!write &&
    !!payload &&
    payload.customerId === targetCustomer &&
    payload.customerId === customer &&
    payload.entries.length > 0 &&
    !payload.entries.some(
      (e) =>
        e.kind === "folder" &&
        target !== null &&
        descendantIds(folders, e.id).includes(target),
    );
  const transfer = async (
    payload: LibraryTransfer,
    target: string | null,
    targetCustomer = customer,
  ) => {
    if (!canMove(payload, target, targetCustomer)) {
      setError("다른 고객사 또는 자기 하위 폴더로는 옮길 수 없습니다.");
      return;
    }
    await mutate(async () => {
      const failed: string[] = [];
      const ms = payload.entries
        .filter((e) => e.kind === "manuscript")
        .map((e) => e.id);
      if (ms.length) {
        const results = await adapter.bulk(ms, "move", target);
        failed.push(
          ...results.filter((r) => !r.ok).map((r) => r.error ?? r.id),
        );
      }
      const fids = payload.entries
        .filter((e) => e.kind === "folder")
        .map((e) => e.id);
      for (const id of fids.filter(
        (id) =>
          !fids.some(
            (other) =>
              other !== id && descendantIds(folders, other).includes(id),
          ),
      )) {
        try {
          await adapter.updateFolder(id, { parentId: target });
        } catch (e) {
          failed.push(libraryError(e));
        }
      }
      if (failed.length) throw new Error(failed.join("\n"));
      setSelected([]);
      setClipboard(null);
      setDialog("");
      setNote(payload.entries.length + "개 항목을 옮겼습니다.");
    });
  };
  const toPayload = (selection: Selection): LibraryTransfer => ({
    customerId: selection.customerId,
    entries: selection.entries.map(({ id, kind }) => ({ id, kind })),
  });
  const drop = async (
    e: DragEvent,
    target: string | null,
    targetCustomer = customer,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setOver(null);
    setExternal(false);
    const raw = e.dataTransfer.getData(libraryDragType);
    if (raw) {
      try {
        await transfer(JSON.parse(raw), target, targetCustomer);
      } catch {
        setError("이동할 항목을 읽지 못했습니다.");
      }
      setDrag(null);
      return;
    }
    if (!write) return;
    try {
      const files = await droppedFiles(e.dataTransfer);
      if (files.length) {
        if (targetCustomer !== customer) go(targetCustomer, target ?? "all");
        else if (target !== (current?.id ?? null))
          go(customer, target ?? "all");
        setUpload({ files });
      }
    } catch (err) {
      setError(libraryError(err));
    }
  };
  const canDelete = (r: Row) => {
    const item = r.item ?? listing.items.find((i) => i.id === r.id);
    return (
      write &&
      (r.kind === "folder" ||
        (!!item && !item.readOnly && !item.locked && !item.jobs))
    );
  };
  const begin = (kind: typeof dialog, items = chosen) => {
    setContext(null);
    setTargets(items);
    setName(kind === "rename" ? (items[0]?.name ?? "") : "");
    setFolderTarget(
      (kind === "create"
        ? current?.targetCustomerId
        : items[0]?.folder?.targetCustomerId) ?? "",
    );
    setMoveTo(null);
    setError("");
    setMemo(null);
    setKeywords(null);
    setDialog(kind);
  };
  const bulk = async (
    action: "ready" | "draft" | "archive" | "unarchive",
    items = chosen,
  ) => {
    setContext(null);
    await mutate(async () => {
      const result = await adapter.bulk(
        items.filter((r) => r.kind === "manuscript").map((r) => r.id),
        action,
      );
      const failed = result.filter((r) => !r.ok);
      if (failed.length)
        throw new Error(failed.map((r) => r.error ?? r.id).join("\n"));
      setNote("원고 상태를 저장했습니다.");
    });
  };
  const create = () =>
    void mutate(async () => {
      const item = await adapter.create({
        customerId: customer,
        folderId: current?.id ?? null,
        body: "# 새 원고\n\n",
        files: [],
      });
      setDoc({ id: item.id, edit: true });
    });
  const submit = () =>
    void mutate(async () => {
      if (dialog === "create" || dialog === "rename") {
        if (!name.trim()) throw new Error("폴더 이름을 입력해 주세요.");
        if (dialog === "create")
          await adapter.createFolder(
            customer,
            current?.id ?? null,
            name.trim(),
            folderTarget || null,
          );
        else await adapter.updateFolder(targets[0].id, { name: name.trim() });
      } else if (dialog === "delete") {
        const failures: string[] = [];
        for (const r of targets) {
          try {
            if (!canDelete(r))
              throw new Error(
                r.name + " · 사용 중인 원고는 삭제할 수 없습니다.",
              );
            if (r.kind === "folder") await adapter.deleteFolder(r.id);
            else await adapter.remove(r.id);
          } catch (e) {
            failures.push(libraryError(e));
          }
        }
        if (failures.length) throw new Error(failures.join("\n"));
        setSelected([]);
      } else if (dialog === "properties" && targets[0]?.kind === "folder") {
        await adapter.updateFolder(targets[0].id, {
          targetCustomerId: folderTarget || null,
        });
      } else if (dialog === "properties" && property.data) {
        await adapter.patch(property.data.id, {
          memo: memo ?? property.data.memo,
          keywords: property.data.locked
            ? property.data.keywords
            : (keywords ?? property.data.keywords.join(","))
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
        });
      }
      setDialog("");
      setNote("변경 내용을 저장했습니다.");
    });
  const cut = (items = chosen) => {
    setClipboard({ customerId: customer, entries: items });
    setContext(null);
    setNote(
      items.length + "개 항목을 잘라냈습니다. 옮길 폴더에서 붙여넣으세요.",
    );
  };
  const keydown = (e: KeyboardEvent) => {
    if (
      (e.target as HTMLElement).closest(
        "input,textarea,select,[contenteditable=true]",
      )
    )
      return;
    const mod = e.ctrlKey || e.metaKey;
    const row =
      rows.find(
        (r) =>
          r.key ===
          (e.target as HTMLElement)
            .closest("[data-entry]")
            ?.getAttribute("data-entry"),
      ) ?? primary;
    if (mod && e.key.toLowerCase() === "a") {
      e.preventDefault();
      setSelected(rows.map((r) => r.key));
    } else if (mod && e.key.toLowerCase() === "x" && write) {
      e.preventDefault();
      cut();
    } else if (mod && e.key.toLowerCase() === "v" && write && clipboard) {
      e.preventDefault();
      void transfer(toPayload(clipboard), current?.id ?? null);
    } else if (e.key === "Backspace" || (e.altKey && e.key === "ArrowLeft")) {
      e.preventDefault();
      up();
    } else if (e.key === "Enter" && row) {
      e.preventDefault();
      open(row);
    } else if (e.key === "F2" && write && row?.kind === "folder") {
      e.preventDefault();
      begin("rename", [row]);
    } else if (
      e.key === "Delete" &&
      write &&
      chosen.length &&
      chosen.every(canDelete)
    ) {
      e.preventDefault();
      begin("delete");
    } else if (e.key === "Escape") {
      setContext(null);
      setSelected([]);
    } else if (e.key === "F10" && e.shiftKey && row) {
      e.preventDefault();
      const rect = (e.target as HTMLElement).getBoundingClientRect();
      setContext({
        x: Math.min(rect.x + 50, window.innerWidth - 245),
        y: Math.min(rect.y + 20, window.innerHeight - 460),
        rows: actionRows(row),
      });
    } else if (
      ["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"].includes(e.key) &&
      !e.altKey
    ) {
      e.preventDefault();
      const index = rows.indexOf(row!),
        next =
          rows[
            Math.max(
              0,
              Math.min(
                rows.length - 1,
                index + (["ArrowUp", "ArrowLeft"].includes(e.key) ? -1 : 1),
              ),
            )
          ];
      if (next) {
        select(next, e);
        area.current
          ?.querySelector<HTMLElement>('[data-entry="' + next.key + '"]')
          ?.focus();
      }
    }
  };
  const menuRows = context?.rows.length ? context.rows : chosen;
  const onlyFiles =
    menuRows.length > 0 && menuRows.every((r) => r.kind === "manuscript");
  const toggleSort = (column: string) => {
    if (sort === column) setDescending(!descending);
    else {
      setSort(column);
      setDescending(false);
    }
  };
  const counts =
    primary?.kind === "folder"
      ? folderCounts(folders, primary.id)
      : current
        ? folderCounts(folders, current.id)
        : folderData.data?.unfiled;
  return (
    <div
      className="ml-screen lex"
      data-library-explorer
      onDragStart={(e) => {
        const raw = e.dataTransfer.getData(libraryDragType);
        if (raw)
          try {
            setDrag(JSON.parse(raw));
          } catch {}
      }}
      onDragEnd={() => {
        setDrag(null);
        setOver(null);
      }}
    >
      <div className="lex-address">
        <div className="lex-history">
          <button
            aria-label="뒤로"
            disabled={cursor === 0 || busy}
            onClick={() => back(-1)}
          >
            <ArrowLeft size={17} />
          </button>
          <button
            aria-label="앞으로"
            disabled={cursor === history.length - 1 || busy}
            onClick={() => back(1)}
          >
            <ArrowRight size={17} />
          </button>
          <button
            aria-label="상위 폴더"
            disabled={folder === "all" || busy}
            onClick={up}
          >
            <ArrowUp size={17} />
          </button>
        </div>
        <nav aria-label="현재 폴더 경로" className="lex-path">
          <HardDrive size={17} />
          <button onClick={() => go(customer, "all")}>
            {libraryRoot(adapter, customer)}
          </button>
          {folder === "none" && (
            <span>
              <ChevronRight size={13} />
              폴더 밖 원고
            </span>
          )}
          {path.map((f) => (
            <span key={f.id}>
              <ChevronRight size={13} />
              <button onClick={() => go(customer, f.id)}>{f.name}</button>
            </span>
          ))}
        </nav>
        <label className="lex-search">
          <Search size={15} />
          <input
            aria-label="원고 검색"
            placeholder="이 폴더에서 검색"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelected([]);
            }}
          />
        </label>
      </div>
      <div className="lex-command">
        {write && (
          <>
            {onRefine || refineUpload ? (
              <details className="mrf-new-menu">
                <summary>＋ 새 원고</summary>
                <div>
                  <button
                    onClick={(e) => {
                      e.currentTarget.closest("details")!.open = false;
                      create();
                    }}
                  >
                    직접 작성
                  </button>
                  <button
                    onClick={(e) => {
                      e.currentTarget.closest("details")!.open = false;
                      setUpload({});
                    }}
                  >
                    파일·폴더 올리기
                  </button>
                  <button
                    onClick={(e) => {
                      e.currentTarget.closest("details")!.open = false;
                      if (refineUpload) setUpload({ channel: "refine" });
                      else onRefine?.(customer, current?.id ?? null);
                    }}
                  >
                    정형화로 올리기
                  </button>
                </div>
              </details>
            ) : (
              <Button size="sm" variant="ghost" onClick={create}>
                <Plus size={16} />새 원고
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => begin("create", [])}
            >
              <FolderPlus size={16} />새 폴더
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setUpload({})}>
              <Upload size={16} />
              올리기
            </Button>
            <span className="lex-separator" />
            <button
              aria-label="잘라내기"
              title="잘라내기 (Ctrl+X)"
              disabled={!chosen.length || busy}
              onClick={() => cut()}
            >
              <Scissors size={16} />
            </button>
            <button
              aria-label="붙여넣기"
              title="붙여넣기 (Ctrl+V)"
              disabled={
                !clipboard ||
                !canMove(
                  clipboard && toPayload(clipboard),
                  current?.id ?? null,
                ) ||
                busy
              }
              onClick={() =>
                clipboard &&
                void transfer(toPayload(clipboard), current?.id ?? null)
              }
            >
              <ClipboardPaste size={16} />
            </button>
          </>
        )}
        {chosen.length > 0 && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              setContext({
                x: Math.min(window.innerWidth - 250, 500),
                y: 230,
                rows: chosen,
              })
            }
          >
            <MoreHorizontal size={16} />
            {chosen.length}개 선택
          </Button>
        )}
        <div className="lex-view">
          <button
            aria-label="자세히 보기"
            aria-pressed={view === "details"}
            onClick={() => setView("details")}
          >
            <List size={17} />
          </button>
          <button
            aria-label="큰 아이콘 보기"
            aria-pressed={view === "icons"}
            onClick={() => setView("icons")}
          >
            <LayoutGrid size={17} />
          </button>
          <button
            aria-label="미리 보기 창"
            aria-pressed={preview}
            onClick={() => setPreview(!preview)}
          >
            <PanelRight size={17} />
          </button>
          <button
            aria-label="새로고침"
            onClick={() => invalidateLibrary(adapter)}
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>
      {(error || listing.error || folderData.error || totals.error) && (
        <p role="alert" className="ml-error">
          {error || listing.error || folderData.error || totals.error}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              listing.retry();
              folderData.retry();
              totals.retry();
            }}
          >
            다시 읽기
          </Button>
        </p>
      )}
      {note && (
        <p className="lex-notice" role="status">
          {note}
          <button aria-label="안내 닫기" onClick={() => setNote("")}>
            <X size={13} />
          </button>
        </p>
      )}
      {scopes.find((s) => s.id === customer)?.readOnly && (
        <p className="lex-notice">
          고객사가 등록한 원고입니다. 읽기 전용 · 이동 불가
        </p>
      )}
      <div className={"lex-workspace " + (preview ? "has-preview" : "")}>
        <aside className="lex-navigation" aria-label="원고함 트리">
          {scopes
            .filter(
              (c) =>
                !!adapter.scopes || !fixedCustomer || c.id === fixedCustomer,
            )
            .map((c) => (
              <section key={c.id}>
                {c.ownerCustomerId && (
                  <p className="ml-caption">
                    고객사 원고함 · 읽기 전용 · 이동 불가
                  </p>
                )}
                {(!adapter.scopes || c.ownerCustomerId !== null) && (
                  <div
                    className={
                      "lex-drive " + (c.id === customer ? "active" : "")
                    }
                    onDragOver={(e) => {
                      if (canMove(drag, null, c.id)) {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                      } else e.dataTransfer.dropEffect = "none";
                    }}
                    onDrop={(e) => void drop(e, null, c.id)}
                  >
                    <button
                      aria-label={
                        c.name +
                        (collapsedDrives.includes(c.id) ? " 펼치기" : " 접기")
                      }
                      onClick={() =>
                        setCollapsedDrives((v) =>
                          v.includes(c.id)
                            ? v.filter((id) => id !== c.id)
                            : [...v, c.id],
                        )
                      }
                    >
                      <ChevronRight
                        size={13}
                        style={{
                          transform: collapsedDrives.includes(c.id)
                            ? ""
                            : "rotate(90deg)",
                        }}
                      />
                    </button>
                    <button onClick={() => go(c.id, "all")}>
                      <HardDrive size={17} />
                      <strong>{c.name}</strong>
                    </button>
                  </div>
                )}
                {!collapsedDrives.includes(c.id) && (
                  <LibraryFolderTree
                    provider={adapter}
                    customerId={c.id}
                    rootLabel={libraryRoot(adapter, c.id)}
                    value={c.id === customer ? folder : ""}
                    onSelect={(id) => go(c.id, id)}
                    onDropItems={
                      write && !c.readOnly
                        ? (e, id) => void drop(e, id, c.id)
                        : undefined
                    }
                    canDrop={(id) => !c.readOnly && canMove(drag, id, c.id)}
                  />
                )}
              </section>
            ))}
        </aside>
        <main
          className={"lex-content " + (external ? "is-external" : "")}
          ref={area}
          tabIndex={0}
          onKeyDown={keydown}
          onContextMenu={(e) => showMenu(e)}
          onDragOver={(e) => {
            if (e.dataTransfer.types.includes("Files") && write) {
              e.preventDefault();
              setExternal(true);
              e.dataTransfer.dropEffect = "copy";
            } else if (canMove(drag, current?.id ?? null)) {
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
            } else e.dataTransfer.dropEffect = "none";
          }}
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node))
              setExternal(false);
          }}
          onDrop={(e) => void drop(e, current?.id ?? null)}
        >
          {!fixedCustomer && (
            <div className="lex-mobile-drive">
              <Select
                label="원고함 위치"
                value={customer}
                onChange={(e) => go(e.target.value, "all")}
              >
                {scopes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </div>
          )}
          <div className="lex-content-head">
            <strong>
              <FolderOpen size={17} />
              {current?.name ??
                (folder === "none"
                  ? "폴더 밖 원고"
                  : libraryRoot(adapter, customer))}
            </strong>
            <Select
              label="원고 상태"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setSelected([]);
              }}
            >
              <option value="all">전체 · 보관 제외</option>
              {Object.entries(libraryStatus).map(([s, label]) => (
                <option value={s} key={s}>
                  {label}
                </option>
              ))}
            </Select>
            <Checkbox
              label="하위 폴더 포함"
              checked={deep}
              onChange={(v) => {
                setDeep(v);
                setSelected([]);
              }}
            />
          </div>
          {view === "details" && (
            <div className="lex-columns">
              {[
                ["name", "이름"],
                ["status", "상태"],
                ["check", "검사"],
                ["media", "사진·영상"],
                ["version", "버전"],
                ["job", "작업·게시"],
              ].map(([k, label]) => (
                <button key={k} onClick={() => toggleSort(k)}>
                  {label}
                  {sort === k && (descending ? " ↓" : " ↑")}
                </button>
              ))}
              <span title="현재 목록 API는 수정일을 제공하지 않습니다">
                수정한 날짜
              </span>
            </div>
          )}
          <div
            className={"lex-entries " + (view === "icons" ? "is-icons" : "")}
            role="listbox"
            aria-label="폴더와 원고"
            aria-multiselectable="true"
          >
            {rows.map((row) => (
              <div
                key={row.key}
                data-entry={row.key}
                data-kind={row.kind}
                role="option"
                aria-selected={selected.includes(row.key)}
                tabIndex={
                  selected.at(-1) === row.key ||
                  (!selected.length && rows[0] === row)
                    ? 0
                    : -1
                }
                className={
                  "lex-entry " +
                  (selected.includes(row.key) ? "selected " : "") +
                  (clipboard?.entries.some((e) => e.key === row.key) &&
                  clipboard.customerId === customer
                    ? "cut "
                    : "") +
                  (over === row.id ? "drop-target" : "")
                }
                draggable={write}
                onClick={(e) => {
                  if (longPress.current) {
                    longPress.current = false;
                    return;
                  }
                  select(row, e);
                  if (mobile() && !e.ctrlKey && !e.shiftKey) open(row);
                }}
                onDoubleClick={() => {
                  if (!mobile()) open(row);
                }}
                onContextMenu={(e) => showMenu(e, row)}
                onPointerDown={(e) => {
                  if (e.pointerType === "touch") {
                    longPress.current = false;
                    timer.current = setTimeout(() => {
                      longPress.current = true;
                      if (!selected.includes(row.key)) {
                        setSelected([...selected, row.key]);
                        setAnchor(row.key);
                      }
                      setContext({
                        x: Math.min(e.clientX, window.innerWidth - 245),
                        y: Math.min(e.clientY, window.innerHeight - 460),
                        rows: selected.includes(row.key)
                          ? chosen
                          : [...chosen, row],
                      });
                    }, 550);
                  }
                }}
                onPointerUp={() => {
                  if (timer.current) clearTimeout(timer.current);
                }}
                onPointerCancel={() => {
                  if (timer.current) clearTimeout(timer.current);
                }}
                onPointerMove={() => {
                  if (timer.current) clearTimeout(timer.current);
                }}
                onDragStart={(e) => {
                  const entries = actionRows(row);
                  if (!selected.includes(row.key)) select(row);
                  const payload = toPayload({ customerId: customer, entries });
                  e.dataTransfer.setData(
                    libraryDragType,
                    JSON.stringify(payload),
                  );
                  e.dataTransfer.effectAllowed = "move";
                  setDrag(payload);
                }}
                onDragOver={(e) => {
                  if (row.kind === "folder") {
                    e.stopPropagation();
                    if (write && e.dataTransfer.types.includes("Files")) {
                      e.preventDefault();
                      setOver(row.id);
                      e.dataTransfer.dropEffect = "copy";
                    } else if (canMove(drag, row.id)) {
                      e.preventDefault();
                      setOver(row.id);
                      e.dataTransfer.dropEffect = "move";
                    } else e.dataTransfer.dropEffect = "none";
                  }
                }}
                onDragLeave={() => setOver(null)}
                onDrop={
                  row.kind === "folder"
                    ? (e) => void drop(e, row.id)
                    : undefined
                }
              >
                <span className="lex-entry-name">
                  {view === "icons" && row.item ? (
                    <Thumbnail adapter={adapter} item={row.item} />
                  ) : row.kind === "folder" ? (
                    <Folder size={view === "icons" ? 48 : 21} />
                  ) : (
                    <FileText size={21} />
                  )}
                  <span className="lex-name-stack">
                    <b className="ml-open">{row.name}</b>
                    {row.item && (
                      <ManuscriptOwnership item={row.item} provider={adapter} />
                    )}
                    {row.folder?.targetCustomerId && (
                      <small
                        className={
                          "ml-target-badge" +
                          (row.folder.targetInherited ? " inherited" : "")
                        }
                      >
                        대상:{" "}
                        {adapter.customers.find(
                          (c) => c.id === row.folder!.targetCustomerId,
                        )?.name ?? row.folder.targetCustomerId}
                        {row.folder.targetInherited ? " · 상속" : ""}
                      </small>
                    )}
                  </span>
                  {row.item?.locked && <LockKeyhole size={13} />}
                </span>
                {row.item ? (
                  <>
                    <span>
                      <Status item={row.item} />
                    </span>
                    <span className={row.item.valid ? "" : "ml-invalid"}>
                      <ReviewSummary
                        errors={row.item.errors}
                        warnings={row.item.warnings}
                        notices={row.item.notices?.length}
                      />
                    </span>
                    <span>
                      {row.item.fileCount - row.item.videos} · {row.item.videos}
                    </span>
                    <span>v{row.item.revision}</span>
                    <span title={row.item.lastJobId ?? ""}>
                      {row.item.postedAt ?? row.item.lastRunStatus ?? "—"}
                    </span>
                    <span>—</span>
                  </>
                ) : (
                  <>
                    <span className="lex-folder-meta">폴더</span>
                    <span className="lex-folder-meta">
                      {Object.entries(folderCounts(folders, row.id)).reduce(
                        (n, [s, v]) => n + (s === "archived" ? 0 : (v ?? 0)),
                        0,
                      )}
                      편
                    </span>
                    <span />
                    <span />
                    <span />
                    <span>—</span>
                  </>
                )}
              </div>
            ))}
          </div>
          {listing.loading && (
            <p role="status" className="lex-empty">
              원고 읽는 중…
            </p>
          )}
          {!listing.loading && !rows.length && (
            <div className="lex-empty">
              <FolderOpen size={40} />
              <strong>이 폴더에는 원고가 없습니다</strong>
              <span>원고를 만들거나 파일을 끌어 놓으세요.</span>
            </div>
          )}
          {listing.items.length < listing.total && (
            <Button
              variant="secondary"
              disabled={listing.loading}
              onClick={() => void listing.loadMore()}
            >
              원고 더 보기
            </Button>
          )}
          {external && (
            <div className="lex-drop-overlay">
              <Upload size={38} />
              <strong>이 폴더에 원고 올리기</strong>
              <span>.md와 사진·영상, 폴더를 놓으세요</span>
            </div>
          )}
          {drag && (
            <div className="lex-drag-hint">
              <Folder size={16} />
              {drag.entries.length}개 이동 · 메타크루 트리의 폴더에 놓으세요
            </div>
          )}
        </main>
        {preview && (
          <aside className="lex-preview" aria-label="미리 보기 창">
            <header>
              <strong>미리 보기</strong>
              <button
                aria-label="미리 보기 닫기"
                onClick={() => setPreview(false)}
              >
                <X size={16} />
              </button>
            </header>
            {chosen.length > 1 ? (
              <div className="lex-preview-info">
                <FileText size={40} />
                <h3>{chosen.length}개 항목 선택</h3>
                <p>
                  도구 모음이나 오른쪽 클릭 메뉴에서 함께 정리할 수 있습니다.
                </p>
              </div>
            ) : primary?.kind === "manuscript" ? (
              <>
                {detail.error && (
                  <p role="alert" className="ml-error">
                    {detail.error}
                    <Button onClick={detail.retry}>다시 읽기</Button>
                  </p>
                )}
                {detail.data ? (
                  <>
                    <div className="lex-preview-info">
                      <Status item={detail.data} />
                      <h3>{detail.data.title}</h3>
                      <ManuscriptOwnership
                        item={detail.data}
                        provider={adapter}
                      />
                      {detail.data.readOnly && (
                        <p>고객사가 등록한 원고입니다</p>
                      )}
                      <p>
                        {detail.data.valid
                          ? "검사 통과"
                          : "형식 오류 " + detail.data.errors + "개"}{" "}
                        · v{detail.data.revision}
                      </p>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => open(primary)}
                      >
                        원고 열기
                      </Button>
                    </div>
                    <LibraryPreview
                      provider={adapter}
                      customerId={customer}
                      body={detail.data.body}
                      files={detail.data.files}
                    />
                    <div className="lex-preview-info">
                      <b>사용 이력 {detail.data.jobs}건</b>
                      {detail.data.uses.slice(0, 3).map((u, i) => (
                        <p key={i}>
                          {u.jobId} · {u.runStatus} · {u.runAt ?? "미실행"}
                        </p>
                      ))}
                    </div>
                  </>
                ) : (
                  <p>미리 보기 읽는 중…</p>
                )}
              </>
            ) : (
              <div className="lex-preview-info">
                <FolderOpen size={46} />
                <h3>{primary?.name ?? current?.name ?? nameOf(customer)}</h3>
                <p>소유: {nameOf(customer)}</p>
                {current?.targetCustomerId && (
                  <p>
                    대상:{" "}
                    {
                      adapter.customers.find(
                        (c) => c.id === current.targetCustomerId,
                      )?.name
                    }
                  </p>
                )}
                {Object.entries(libraryStatus).map(([s, label]) => (
                  <p className="lex-count" key={s}>
                    <span>{label}</span>
                    <b>{counts?.[s as LibraryStatus] ?? 0}</b>
                  </p>
                ))}
              </div>
            )}
          </aside>
        )}
      </div>
      <footer className="lex-status">
        <span>
          항목 {listing.total + rows.filter((r) => r.kind === "folder").length}
          개 · {selected.length}개 선택 · 준비됨 {totals.data ?? "—"}
        </span>
        <span>
          {busy ? "저장 중…" : write ? "불러온 항목 기준 정렬" : "보기 전용"} ·{" "}
          {listing.items.length}/{listing.total}편
        </span>
      </footer>
      {context && (
        <div
          ref={menu}
          className="lex-context"
          role="menu"
          aria-label="항목 메뉴"
          style={{ left: Math.max(8, context.x), top: Math.max(8, context.y) }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setContext(null);
              area.current?.focus();
            }
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault();
              const buttons = Array.from(
                menu.current?.querySelectorAll<HTMLButtonElement>(
                  "button:not(:disabled)",
                ) ?? [],
              );
              const i = buttons.indexOf(
                document.activeElement as HTMLButtonElement,
              );
              buttons[
                (i + (e.key === "ArrowDown" ? 1 : -1) + buttons.length) %
                  buttons.length
              ]?.focus();
            }
          }}
        >
          <button
            role="menuitem"
            disabled={menuRows.length !== 1}
            onClick={() => open(menuRows[0])}
          >
            열기 <kbd>Enter</kbd>
          </button>
          <button
            role="menuitem"
            disabled={
              !canSend ||
              !onlyFiles ||
              menuRows.some(
                (r) =>
                  r.item?.locked || !r.item?.valid || r.item.status !== "ready",
              )
            }
            onClick={() => {
              setSendIds(menuRows.map((r) => r.id));
              setContext(null);
            }}
          >
            작업으로 보내기
          </button>
          {write && !menuRows.some((r) => r.item?.readOnly) && (
            <>
              <button
                role="menuitem"
                disabled={
                  !write ||
                  menuRows.length !== 1 ||
                  menuRows[0].kind !== "folder"
                }
                onClick={() => begin("rename", menuRows)}
              >
                이름 바꾸기 <kbd>F2</kbd>
              </button>
              <hr />
              <button
                role="menuitem"
                disabled={!write || !menuRows.length}
                onClick={() => cut(menuRows)}
              >
                잘라내기 <kbd>Ctrl+X</kbd>
              </button>
              <button
                role="menuitem"
                disabled={
                  !clipboard ||
                  !canMove(
                    clipboard && toPayload(clipboard),
                    menuRows.length === 1 && menuRows[0].kind === "folder"
                      ? menuRows[0].id
                      : (current?.id ?? null),
                  )
                }
                onClick={() => {
                  setContext(null);
                  if (clipboard)
                    void transfer(
                      toPayload(clipboard),
                      menuRows.length === 1 && menuRows[0].kind === "folder"
                        ? menuRows[0].id
                        : (current?.id ?? null),
                    );
                }}
              >
                붙여넣기 <kbd>Ctrl+V</kbd>
              </button>
              <button
                role="menuitem"
                disabled={!write || !menuRows.length}
                onClick={() => begin("move", menuRows)}
              >
                옮길 위치 고르기
              </button>
              <hr />
              {(["ready", "draft", "archive", "unarchive"] as const).map(
                (action, i) => (
                  <button
                    role="menuitem"
                    key={action}
                    disabled={
                      !write ||
                      !onlyFiles ||
                      ((action === "ready" || action === "draft") &&
                        menuRows.some((r) => r.item?.locked))
                    }
                    onClick={() => void bulk(action, menuRows)}
                  >
                    {["준비됨으로", "작성 중으로", "보관", "되돌리기"][i]}
                  </button>
                ),
              )}
              <hr />
              <button
                role="menuitem"
                disabled={
                  !write || !menuRows.length || !menuRows.every(canDelete)
                }
                onClick={() => begin("delete", menuRows)}
              >
                삭제 <kbd>Delete</kbd>
              </button>
            </>
          )}
          <button
            role="menuitem"
            disabled={menuRows.length !== 1}
            onClick={() => begin("properties", menuRows)}
          >
            속성
          </button>
        </div>
      )}
      <Dialog
        open={!!doc}
        title="원고 상세"
        size="detail"
        onOpenChange={(v) => {
          if (!v) closeDoc();
        }}
      >
        {doc && (
          <>
            {confirmClose && (
              <div className="ml-warning">
                <p>저장하지 않은 원고가 있습니다.</p>
                <Button
                  variant="secondary"
                  onClick={() => setConfirmClose(false)}
                >
                  계속 편집
                </Button>
                <Button
                  variant="danger"
                  onClick={() => {
                    setDoc(null);
                    setDirty(false);
                    onDirtyChange?.(false);
                    setConfirmClose(false);
                  }}
                >
                  변경 버리고 닫기
                </Button>
              </div>
            )}
            {renderDocument({
              provider: adapter,
              customerId: customer,
              id: doc.id,
              edit: !!doc.edit,
              onDirtyChange: (v) => {
                setDirty(v);
                onDirtyChange?.(v);
              },
            })}
          </>
        )}
      </Dialog>
      {upload &&
        renderUpload({
          provider: adapter,
          customerId: customer,
          folderId: current?.id ?? null,
          files: upload.files,
          channel: upload.channel ?? initialUploadChannel,
          onClose: () => setUpload(null),
        })}
      {sendIds &&
        renderSend({
          provider: adapter,
          customerId: customer,
          ids: sendIds,
          step: initialSendStep,
          onClose: () => setSendIds(null),
        })}
      <Dialog
        open={!!dialog}
        title={
          {
            create: "새 폴더",
            rename: "이름 바꾸기",
            delete: "선택한 항목 삭제",
            move: "옮길 위치 고르기",
            properties: "속성",
          }[dialog || "properties"]
        }
        onOpenChange={(v) => {
          if (!v && !busy) setDialog("");
        }}
        presentation={dialog === "move" ? "sheet" : "dialog"}
        footer={
          <DialogActions
            secondary={{
              label: "취소",
              disabled: busy,
              onClick: () => setDialog(""),
            }}
            primary={
              dialog === "properties" && (!write || !!property.data?.readOnly)
                ? undefined
                : {
                    label:
                      dialog === "delete"
                        ? "삭제"
                        : dialog === "move"
                          ? "여기로 이동"
                          : "저장",
                    tone: dialog === "delete" ? "danger" : "default",
                    disabled:
                      busy ||
                      (dialog === "move" &&
                        !canMove(
                          toPayload({
                            customerId: customer,
                            entries: targets.length ? targets : chosen,
                          }),
                          moveTo,
                        )),
                    onClick: () =>
                      dialog === "move"
                        ? void transfer(
                            toPayload({
                              customerId: customer,
                              entries: targets.length ? targets : chosen,
                            }),
                            moveTo,
                          )
                        : submit(),
                  }
            }
          />
        }
      >
        {error && (
          <p role="alert" className="ml-error">
            {error}
          </p>
        )}
        {(dialog === "create" || dialog === "rename") && (
          <Field
            label="폴더 이름"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
          />
        )}
        {(dialog === "create" ||
          (dialog === "properties" && targets[0]?.kind === "folder")) &&
          (() => {
            const parent =
              dialog === "create"
                ? current
                : folders.find((f) => f.id === targets[0]?.folder?.parentId);
            return (
              <div className="ml-folder-target">
                <Select
                  label="대상 고객사(선택)"
                  value={parent?.targetCustomerId ?? folderTarget}
                  disabled={!write || !!parent?.targetCustomerId}
                  onChange={(e) => setFolderTarget(e.target.value)}
                >
                  <option value="">없음</option>
                  {adapter.customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
                {parent?.targetCustomerId && (
                  <p className="ml-caption">
                    상위 폴더의 대상을 상속합니다. 여기서는 변경할 수 없습니다.
                  </p>
                )}
              </div>
            );
          })()}
        {dialog === "delete" && (
          <>
            <p>
              {targets.length}개 항목을 삭제합니다. 사용 이력이 있거나 잠긴
              원고는 삭제할 수 없습니다.
            </p>
            {targets.some(
              (r) =>
                r.kind === "manuscript" &&
                (r.item ?? listing.items.find((i) => i.id === r.id))
                  ?.refineOrigin,
            ) && (
              <p className="lex-notice">
                연결된 정형화 작업은 검수 대기로 돌아갑니다(원본·시도 기록은
                남음).
              </p>
            )}
            {targets.some((r) => r.kind === "folder") && (
              <p>폴더 안의 원고와 하위 폴더는 한 단계 위로 옮겨집니다.</p>
            )}
          </>
        )}
        {dialog === "move" && (
          <>
            <p>{nameOf(customer)} 안에서 이동합니다.</p>
            <LibraryFolderTree
              provider={adapter}
              customerId={customer}
              value={moveTo ?? "all"}
              onSelect={(id) =>
                setMoveTo(id === "all" || id === "none" ? null : id)
              }
              rootLabel={nameOf(customer) + " 전체"}
            />
          </>
        )}
        {dialog === "properties" &&
          (targets[0]?.kind === "folder" ? (
            <p>{targets[0].name} · 폴더</p>
          ) : property.data ? (
            <>
              <Field
                label="메모"
                defaultValue={property.data.memo ?? ""}
                readOnly={!write}
                onChange={(e) => setMemo(e.target.value)}
              />
              <Field
                label="추천 SEO 키워드"
                defaultValue={property.data.keywords.join(", ")}
                readOnly={!write || property.data.locked}
                onChange={(e) => setKeywords(e.target.value)}
              />
            </>
          ) : (
            <p>{property.error || "속성을 읽는 중…"}</p>
          ))}
      </Dialog>
    </div>
  );
}
